import { StoreEntryChoices } from "../../components/dtp/StoreEntryChoices";
import { trackStoreEvent } from '../../lib/dtpAnalytics';
import { track } from '../../lib/analytics';
import type { CustomStoreDesign } from '@bloom/shared';
import { usesEducationalStore } from '@bloom/shared';
import { CustomStoreView } from '../../components/store/custom/CustomStoreView';
import { StorefrontTheme } from '../../lib/storefrontTheme';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Plus, Check, Loader2, Info, X, MapPin, Repeat } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { doseFamilyKey, useDtpCartStore } from '../../store/useDtpCartStore';
import { CATEGORY_ORDER, getDisplayCategory } from '../../lib/dtpCategories';

export function DtpShop() {
  const { physicianId } = useDtpParams<{ physicianId: string }>();
  const navigate = useNavigate();

  const [entryFlow, setEntryFlow] = useState<import("@bloom/shared").StoreEntryFlow>();
  const [intakePrograms, setIntakePrograms] = useState<import("@bloom/shared").PublicIntakeProgram[]>([]);
  const [customDesign, setCustomDesign] = useState<CustomStoreDesign | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [clinicName, setClinicName] = useState('');
  const [coverage, setCoverage] = useState<import('@bloom/shared').StoreCoverage | null>(null);
  const [storeTheme, setStoreTheme] = useState<StorefrontTheme | null>(null);
  const [membershipEnabled, setMembershipEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  const { items, addItem, swapItem, removeItem, hasItem, applyClinicFromCatalog } = useDtpCartStore();
  const subtotal = items.reduce((sum, i) => sum + i.price, 0);

  useEffect(() => {
    if (!physicianId) return;
    setLoading(true);
    dtpApi.getCatalog(physicianId)
      .then((res: any) => {
        if (res.success && res.data) {
          // Educational variant lives at /learn — redirect there
          if (usesEducationalStore(res.data.clinic || {})) {
            navigate(dtpPath(physicianId, `/learn`), { replace: true });
            return;
          }
          setCustomDesign(res.data.clinic.storefrontVariant === 'custom' ? res.data.clinic.customDesign : null);
          setProducts(res.data.products);
          setEntryFlow(res.data.clinic.entryFlow);
          setIntakePrograms(res.data.intakePrograms || []);
          setClinicName(res.data.clinic.name);
          setCoverage(res.data.clinic.coverage || null);
          setStoreTheme(res.data.clinic.theme || null);
          setMembershipEnabled(!!res.data.clinic.membershipEnabled);
          applyClinicFromCatalog(res.data.clinic);
        } else {
          setError('Failed to load products');
        }
      })
      .catch(() => setError('Failed to load products'))
      .finally(() => setLoading(false));
  }, [physicianId, navigate]);

  // Build category list from actual products
  const availableCategories = useMemo(() => {
    const catSet = new Set<string>();
    products.forEach(p => {
      catSet.add(getDisplayCategory(p.categories || []));
    });
    // Sort by defined order, then alphabetical for unknowns
    return [...catSet].sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [products]);

  // Group and filter products
  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(p =>
        p.displayName.toLowerCase().includes(q) ||
        p.drugName.toLowerCase().includes(q)
      );
    }

    if (activeCategory) {
      filtered = filtered.filter(p =>
        getDisplayCategory(p.categories || []) === activeCategory
      );
    }

    return filtered;
  }, [products, search, activeCategory]);

  // Group filtered products by category for sectioned display
  const groupedProducts = useMemo(() => {
    if (activeCategory || search) {
      // When filtering, show flat list
      return null;
    }
    const groups: Record<string, any[]> = {};
    products.forEach(p => {
      const cat = getDisplayCategory(p.categories || []);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    // Sort groups by defined order
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return sorted;
  }, [products, activeCategory, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const renderProductCard = (product: any) => {
    const inCart = hasItem(product._id);
    const isExpanded = expandedProduct === product._id;

    // Check if adding this product would violate category limits
    const productCategory = getDisplayCategory(product.categories || []);
    // One weight-loss treatment per order. Rather than a dead end, offer to
    // swap out the one already in the cart — that's also how a patient moves
    // to a different dose here, since each dose is its own product.
    const cartWeightLoss = productCategory === 'Weight Loss' && !inCart
      ? items.find(i => {
        const cartProduct = products.find(p => p._id === i.productId);
        return cartProduct && getDisplayCategory(cartProduct.categories || []) === 'Weight Loss';
      })
      : undefined;
    const cartItem = { productId: product._id, productName: product.displayName, price: product.price, family: doseFamilyKey(product) };

    return (
      <div key={product._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900">{product.displayName}</h3>
            {(product.description || product.productDetails) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedProduct(isExpanded ? null : product._id);
                  if (!isExpanded) track('product_viewed', { productId: product._id });
                }}
                className="p-1 text-gray-400 hover:text-blue-600 flex-shrink-0 transition-colors"
                title="More info"
              >
                {isExpanded ? <X className="w-4 h-4" /> : <Info className="w-4 h-4" />}
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {product.concentration} &middot; {product.format}
          </p>

          {isExpanded && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 leading-relaxed">
              {product.description || 'No additional details available.'}
            </div>
          )}

          {cartWeightLoss && (
            <p className="text-xs text-gray-500 mt-2">
              One weight-loss treatment per order — swapping replaces {cartWeightLoss.productName}.
            </p>
          )}

          {product.estimatedShipDays > 0 && (
            <p className="text-xs text-gray-400 mt-2">Ships in ~{product.estimatedShipDays} days</p>
          )}

          <div className="mt-auto pt-4 flex items-center justify-between">
            <span className="text-xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
            {customDesign?.productPages?.some(page => page.productId === product._id) ? (
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium" onClick={() => navigate(dtpPath(physicianId, `/products/${customDesign.productPages!.find(page => page.productId === product._id)!.slug}`))}>View treatment</button>
            ) : inCart ? (
              <button
                onClick={() => removeItem(product._id)}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-red-50 hover:text-red-700 transition-colors group"
              >
                <Check className="w-4 h-4 group-hover:hidden" />
                <span className="group-hover:hidden">In Cart</span>
                <span className="hidden group-hover:inline">Remove</span>
              </button>
            ) : cartWeightLoss ? (
              <button
                onClick={() => {
                  swapItem(cartWeightLoss.productId, cartItem);
                  trackStoreEvent('add_to_cart', { productId: product._id, source: 'shop-swap' });
                }}
                title={`You can order one weight-loss treatment at a time. This replaces ${cartWeightLoss.productName} in your cart.`}
                className="flex items-center gap-1.5 px-4 py-2 border border-blue-600 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
              >
                <Repeat className="w-4 h-4" />
                Swap in
              </button>
            ) : (
              <button
                onClick={() => {
                  addItem(cartItem);
                  trackStoreEvent('add_to_cart', { productId: product._id, source: 'shop' });
                }}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <StoreEntryChoices flow={entryFlow} programs={intakePrograms} physicianId={physicianId || ""}>
      {customDesign && <CustomStoreView design={customDesign} clinicName={clinicName} coverage={coverage} products={products} assetUrl={id => '/api/dtp/custom-store/' + physicianId + '/assets/' + id} onProduct={id => { const page = customDesign.productPages?.find(p => p.productId === id); if (page) { navigate(dtpPath(physicianId, `/products/${page.slug}`)); return; } setSearch(products.find(p => p._id === id)?.displayName || ''); setActiveCategory(null); document.getElementById('clinic-products')?.scrollIntoView({ behavior: 'instant', block: 'start' }); }} />}
      <div id="clinic-products" className="scroll-mt-20" />
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          {clinicName && (
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{customDesign ? 'All treatments' : storeTheme?.headline || clinicName}</h1>
          )}
          <p className="text-gray-500 text-sm">{customDesign ? 'Browse products and add to your cart.' : storeTheme?.description || 'Browse products and add to your cart.'}</p>
          {coverage && (
            <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-gray-600">
              <MapPin className="w-3.5 h-3.5" />{coverage.label}
            </p>
          )}
        </div>
        {membershipEnabled && (
          <button
            onClick={() => navigate(dtpPath(physicianId, `/membership`))}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors flex-shrink-0"
          >
            Membership
          </button>
        )}
      </div>

      {/* Search + Category Filters */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); if (e.target.value) setActiveCategory(null); }}
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {availableCategories.length > 1 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setActiveCategory(null); setSearch(''); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                !activeCategory && !search
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              All
            </button>
            {availableCategories.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat === activeCategory ? null : cat); setSearch(''); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  activeCategory === cat
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Products */}
      {(activeCategory || search) ? (
        // Flat filtered view
        filteredProducts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {search ? 'No products match your search.' : 'No products in this category.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map(renderProductCard)}
          </div>
        )
      ) : (
        // Grouped by category
        groupedProducts && groupedProducts.length > 0 ? (
          <div className="space-y-10">
            {groupedProducts.map(([category, prods]) => (
              <section key={category}>
                <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                  {category}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {prods.map(renderProductCard)}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">No products available.</div>
        )
      )}

      {/* Cart bar */}
      {items.length > 0 && (
        <>
        {/* Room for the fixed cart bar, so it never covers the last row of products. */}
        <div aria-hidden="true" className="h-24" />
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-20" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div>
              <span className="text-sm text-gray-600">{items.length} item{items.length !== 1 ? 's' : ''}</span>
              <span className="mx-2 text-gray-300">|</span>
              <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
              <span className="text-sm text-gray-500 ml-1">+ shipping</span>
            </div>
            <button
              onClick={() => navigate(dtpPath(physicianId, `/checkout`))}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <ShoppingCart className="w-4 h-4" />
              Checkout
            </button>
          </div>
        </div>
        </>
      )}
      </StoreEntryChoices>
    </div>
  );
}
